import { jsx as _jsx } from "react/jsx-runtime";
import { Chart, registerables } from 'chart.js';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar, Bubble, Doughnut, Line, Pie } from 'react-chartjs-2';
import { BaseNumberChart } from '../reporting_number_chart/base_number_chart.component';
Chart.register(...registerables, annotationPlugin);
const COMMON_OPTIONS = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        title: {
            display: false,
        },
        legend: {
            position: 'right',
        },
    },
};
const PIE_OPTIONS = {
    ...COMMON_OPTIONS,
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            labels: {
                usePointStyle: true,
            },
        },
    },
};
const BAR_OPTIONS = {
    ...COMMON_OPTIONS,
    scales: {
        x: {
            grid: {
                display: false,
            },
        },
    },
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            display: false,
        },
    },
};
const STACKED_BAR_OPTIONS = {
    ...COMMON_OPTIONS,
    scales: {
        x: {
            stacked: true,
            grid: {
                display: false,
            },
        },
        y: {
            stacked: true,
        },
    },
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            labels: {
                usePointStyle: true,
            },
        },
    },
};
const BUBBLE_OPTIONS = {
    ...COMMON_OPTIONS,
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            labels: {
                usePointStyle: true,
            },
        },
    },
};
const HORIZONTAL_BAR_OPTIONS = {
    ...COMMON_OPTIONS,
    indexAxis: 'y',
    scales: {
        x: {
            stacked: true,
            grid: {
                display: false,
            },
        },
        y: {
            stacked: true,
            grid: {
                display: false,
            },
        },
    },
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            labels: {
                usePointStyle: true,
            },
        },
        tooltip: {
            mode: 'index',
            intersect: false,
        },
    },
    layout: {
        padding: {
            left: 10,
            right: 10,
            top: 10,
            bottom: 10,
        },
    },
};
const GAUGE_OPTIONS = {
    ...COMMON_OPTIONS,
    aspectRatio: 2,
    circumference: 180,
    rotation: -90,
    plugins: {
        ...COMMON_OPTIONS.plugins,
        legend: {
            ...COMMON_OPTIONS.plugins.legend,
            display: false,
        },
        annotation: {
            annotations: {
                gaugeLabel: {
                    type: 'label',
                    position: 'bottom',
                    xValue: '50%',
                    yValue: 'bottom',
                    content: (context) => {
                        const { chart } = context;
                        if (!chart || !chart.data || !chart.data.datasets || !chart.data.datasets[0]) {
                            return '0';
                        }
                        return Math.round(chart.data.datasets[0].data[0]).toString();
                    },
                    font: {
                        size: (context) => {
                            const { chartArea } = context.chart;
                            if (!chartArea) {
                                return 20; // Default if chartArea is not available
                            }
                            return Math.max(16, chartArea.height * 0.08); // At least 16px, 8% of chart height
                        },
                        weight: 'bold',
                    },
                    color: (context) => {
                        const { chart } = context;
                        if (!chart || !chart.data || !chart.data.datasets || !chart.data.datasets[0]) {
                            return '#8CD610';
                        }
                        return chart.data.datasets[0].backgroundColor[0];
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: 8,
                    borderRadius: 6,
                    xAdjust: 0,
                    yAdjust: (context) => {
                        const { chartArea } = context.chart;
                        if (!chartArea) {
                            return 25; // Default if chartArea is not available
                        }
                        return chartArea.height * 0.15; // 15% of chart area height
                    },
                },
                gaugeTitle: {
                    type: 'label',
                    position: 'bottom',
                    xValue: '50%',
                    yValue: 'bottom',
                    content: (context) => {
                        const { chart } = context;
                        if (!chart || !chart.data || !chart.data.datasets || !chart.data.datasets[0]) {
                            return 'Value';
                        }
                        return chart.data.datasets[0].label || 'Value';
                    },
                    font: {
                        size: (context) => {
                            const { chartArea } = context.chart;
                            if (!chartArea) {
                                return 12; // Default if chartArea is not available
                            }
                            return Math.max(10, chartArea.height * 0.05); // At least 10px, 5% of chart height
                        },
                        weight: 'normal',
                    },
                    color: '#666',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    padding: 4,
                    borderRadius: 3,
                    xAdjust: 0,
                    yAdjust: (context) => {
                        const { chartArea } = context.chart;
                        if (!chartArea) {
                            return 45; // Default if chartArea is not available
                        }
                        return chartArea.height * 0.25; // 30% of chart area height
                    },
                },
            },
        },
    },
};
function renderChart({ type, data, onClick, options }) {
    const pieOptions = { ...PIE_OPTIONS, ...options, onClick };
    const doughnutOptions = { ...PIE_OPTIONS, ...options, onClick };
    const barOptions = { ...BAR_OPTIONS, ...options, onClick };
    const stackedBarOptions = { ...STACKED_BAR_OPTIONS, ...options, onClick };
    const lineOptions = { ...BAR_OPTIONS, ...options, onClick };
    const bubbleOptions = { ...BUBBLE_OPTIONS, ...options, onClick };
    const stackedAreaOptions = { ...STACKED_BAR_OPTIONS, ...options, onClick };
    const horizontalBarOptions = { ...HORIZONTAL_BAR_OPTIONS, ...options, onClick };
    const gaugeOptions = { ...GAUGE_OPTIONS, ...options, onClick };
    switch (type) {
        case 'pie':
            return _jsx(Pie, { data: data, onClick: onClick, options: pieOptions });
        case 'doughnut':
            return (_jsx(Doughnut, { data: data, onClick: onClick, options: doughnutOptions }));
        case 'bar':
            return _jsx(Bar, { data: data, onClick: onClick, options: barOptions });
        case 'stackedBar':
            return (_jsx(Bar, { data: data, onClick: onClick, options: stackedBarOptions }));
        case 'horizontalBar':
            return (_jsx(Bar, { data: data, onClick: onClick, options: horizontalBarOptions }));
        case 'line':
            return _jsx(Line, { data: data, onClick: onClick, options: lineOptions });
        case 'bubble':
            return (_jsx(Bubble, { data: data, onClick: onClick, options: bubbleOptions }));
        case 'stackedArea':
            return (_jsx(Line, { data: data, onClick: onClick, options: stackedAreaOptions }));
        case 'gauge':
            return (_jsx(Doughnut, { data: data, onClick: onClick, options: gaugeOptions }));
        case 'number':
            return _jsx(BaseNumberChart, { data: data });
        default:
            return _jsx("div", {});
    }
}
export const ReportingChart = ({ type = 'bar', data, onClick, options = {} }) => (_jsx("div", { className: 'flex flex-1 min-w-0', children: renderChart({ type, data, onClick, options }) }));
